import React, { useState } from "react";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { motion } from "framer-motion"; // Import motion
import { useDonation } from "../Components/DonationContext";
import { getApiBase } from "../utils/paths";
import "./ContactUs.css";

// Small inline icons for the fields that don't already have a theme icon-font class.
// currentColor lets them inherit color from their wrapper.
const IconUser = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" />
  </svg>
);

const IconPhoneSmall = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h4l2 5-2.5 1.5a12 12 0 0 0 6 6L15 14l5 2v4a2 2 0 0 1-2 2C9.6 22 2 14.4 2 6a2 2 0 0 1 2-2Z" />
  </svg>
);

const IconTag = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 12 12.6 19.4a2 2 0 0 1-2.8 0L4 13.6V4h9.6l6.4 6.4a2 2 0 0 1 0 2.8Z" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

const IconMessage = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
  </svg>
);

const IconSend = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4Z" />
  </svg>
);

const ContactUs = () => {
  const { openDonation } = useDonation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = Object.fromEntries(formData.entries());

    const name = String(payload.name || "").trim();
    const email = String(payload.email || "").trim();
    const phone = String(payload.phone || "").trim();
    const subject = String(payload.subject || "").trim();
    const message = String(payload.message || "").trim();

    if (!name || !email || !phone || !message) {
      setSubmitStatus({ type: "error", message: "Please fill in your name, email, phone, and message." });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSubmitStatus({ type: "error", message: "Please enter a valid email address." });
      return;
    }

    if (phone.replace(/\D/g, "").length < 8) {
      setSubmitStatus({ type: "error", message: "Please enter a valid phone number." });
      return;
    }

    if (message.length < 10) {
      setSubmitStatus({ type: "error", message: "Your message must be at least 10 characters long." });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      const res = await fetch(`${getApiBase()}/public/contact.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          subject: subject || "General enquiry",
          message,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Unable to send your message. Please try again.");
      }

      setSubmitStatus({ type: "success", message: data.message || "Your message has been sent successfully." });
      form.reset();
    } catch (error) {
      setSubmitStatus({ type: "error", message: error.message || "Unable to send your message." });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants for the container of the info cards
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2, // This creates the cascade effect
      },
    },
  };

  // Animation variants for each individual info card
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  // A reusable animation variant for sections that slide up and fade in
  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
      },
    },
  };

  return (
    <>
      <Header />
      <div className="page-wrapper">
        {/*Start Contact Hero*/}
        <section className="contact-hero">
          <div className="contact-hero__dots" />
          <div className="container">
            <div className="row align-items-center">
              <motion.div
                className="col-lg-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="contact-hero__eyebrow">
                  Get In Touch <i />
                </span>
                <h1 className="contact-hero__title">
                  We'd Love To <span>Hear</span> From You
                </h1>
                <p className="contact-hero__text">
                  Have a question, suggestion, or want to get involved? We're
                  here to help and would love to connect with you.
                </p>
                <button
                  type="button"
                  className="vh-donate-btn contact-hero__donate-btn"
                  onClick={openDonation}
                >
                  <i className="fa-solid fa-heart" aria-hidden="true" />
                  Donate Now
                </button>
              </motion.div>
              <motion.div
                className="col-lg-6"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="contact-hero__media">
                  <img
                    src="assets/images/backgrounds/contact.png"
                    alt="Let's Connect"
                    className="contact-hero__img"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        {/*End Contact Hero*/}

        {/*Start Contact Page Info*/}
        <section className="contact-page-info">
          <div className="container">
            <motion.div
              className="contact-info-card"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/*Start Contact Page Info Single*/}
              <motion.div className="contact-info-card__item" variants={itemVariants}>
                <div className="icon-box">
                  <span className="icon-gps" />
                </div>
                <div className="content-box">
                  <h3>Address</h3>
                  <p>
                    Yubi Foods, Rasulgarh <br />
                    Bhubaneswar, Odisha 751010
                  </p>
                </div>
              </motion.div>
              {/*End Contact Page Info Single*/}

              {/*Start Contact Page Info Single*/}
              <motion.div className="contact-info-card__item" variants={itemVariants}>
                <div className="icon-box">
                  <span className="icon-telephone1" />
                </div>
                <div className="content-box">
                  <h3>Phone</h3>
                  <p>
                    <a href="tel:1234567890">+91-674-2548306</a>
                  </p>
                </div>
              </motion.div>
              {/*End Contact Page Info Single*/}

              {/*Start Contact Page Info Single*/}
              <motion.div className="contact-info-card__item" variants={itemVariants}>
                <div className="icon-box">
                  <span className="icon-email-1" />
                </div>
                <div className="content-box">
                  <h3>Email</h3>
                  <p>
                    <a href="mailto:exampal@gmail.com">info@vikash.org</a>
                  </p>
                </div>
              </motion.div>
              {/*End Contact Page Info Single*/}
            </motion.div>
          </div>
        </section>
        {/*End Contact Page Info*/}

        {/*Start Map + Form*/}
        <section className="contact-main-section">
          <div className="container">
            <div className="row g-4 align-items-stretch">
              {/*Start Google Map One*/}
              <motion.div
                className="col-lg-6"
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="contact-map-card">
                  <iframe
                    loading="lazy"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3742.141400372138!2d85.851542!3d20.2944135!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a190b0dc87c8485%3A0x9baf51bb9cd08a17!2sYUBI%20(Eat%20Healthy%20%3A%20Live%20Young)!5e0!3m2!1sen!2sin!4v1758955107848!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    title="YuBi kitchen"
                    aria-label="Rasulgarh, Bhubaneswar"
                  ></iframe>
                </div>
              </motion.div>
              {/*End Google Map One*/}

              {/*Start Contact Page Form*/}
              <motion.div
                className="col-lg-6"
                variants={sectionVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="contact-form-card">
                  <div className="contact-form-card__dots" />
                  <div className="title-box">
                    <span className="title-box__icon">
                      <IconSend />
                    </span>
                    <h2>Send Us A Message</h2>
                  </div>
                  <form
                    id="contact-form"
                    className="contact-form-validated contact-page__form"
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <div className="row">
                      <div className="col-xl-12 col-lg-12 col-md-12">
                        <div className="contact-page__input-box">
                          <span className="contact-page__input-icon">
                            <IconUser />
                          </span>
                          <input
                            type="text"
                            name="name"
                            placeholder="Your Name"
                            required=""
                          />
                        </div>
                      </div>
                      <div className="col-xl-6 col-lg-6 col-md-6">
                        <div className="contact-page__input-box">
                          <span className="contact-page__input-icon">
                            <IconPhoneSmall />
                          </span>
                          <input
                            type="text"
                            placeholder="Phone Number"
                            name="phone"
                            required=""
                          />
                        </div>
                      </div>
                      <div className="col-xl-6 col-lg-6 col-md-6">
                        <div className="contact-page__input-box">
                          <span className="contact-page__input-icon">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M4 6h16v12H4z" />
                            <path d="m4 7 8 6 8-6" />
                          </svg>
                          </span>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            required=""
                          />
                        </div>
                      </div>
                      <div className="col-xl-6 col-lg-6 col-md-6">
                        <div className="contact-page__input-box">
                          <span className="contact-page__input-icon">
                            <IconTag />
                          </span>
                          <input type="text" placeholder="Subject" name="subject" />
                        </div>
                      </div>
                      <div className="col-xl-12">
                        <div className="contact-page__input-box text-message-box">
                          <span className="contact-page__input-icon contact-page__input-icon--top">
                            <IconMessage />
                          </span>
                          <textarea
                            name="message"
                            placeholder="Type Your Message"
                            required=""
                            defaultValue={""}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="result" role="status" aria-live="polite">
                      {submitStatus.message ? (
                        <div
                          style={{
                            marginTop: "1rem",
                            padding: "0.75rem 1rem",
                            borderRadius: "8px",
                            fontSize: "0.95rem",
                            background:
                              submitStatus.type === "success" ? "rgba(22, 163, 74, 0.08)" : "rgba(239, 68, 68, 0.08)",
                            color:
                              submitStatus.type === "success" ? "#166534" : "#b91c1c",
                            border: `1px solid ${submitStatus.type === "success" ? "rgba(22, 163, 74, 0.2)" : "rgba(239, 68, 68, 0.2)"}`,
                          }}
                        >
                          {submitStatus.message}
                        </div>
                      ) : null}
                    </div>
                    <div className="contact-page__btn-box">
                      <button
                        type="submit"
                        className="thm-btn contact-page__btn"
                        data-loading-text="Please wait..."
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Sending..." : "Send A Message"}
                        <span className="icon-diagonal-arrow1">
                          <IconSend />
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
              {/*End Contact Page Form*/}
            </div>
          </div>
        </section>
        {/*End Map + Form*/}
      </div>

      <Footer />
      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};
export default ContactUs;